import { useCallback } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

/**
 * The selected tab of a detail page, kept in the router's `?tab=` search param
 * so a tab can be deep-linked and survives a reload.
 *
 * An unknown or missing value falls back to `fallback`. Switching tabs replaces
 * the history entry: tabs are a view of one page, so the back button should
 * leave the page, not step through every tab visited.
 */
export function useRouteTab<T extends string>(allowed: readonly T[], fallback: T) {
  const search = useSearch({ strict: false }) as { tab?: unknown }
  const navigate = useNavigate()

  const tab = allowed.find((value) => value === search.tab) ?? fallback

  const setTab = useCallback(
    (next: string) => {
      navigate({ search: ((prev: Record<string, unknown>) => ({ ...prev, tab: next })) as never, replace: true })
    },
    [navigate],
  )

  return [tab, setTab] as const
}
